import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { Form } from 'softcat:components/form.tsx';
import { CatalogueState } from 'softcat:config/catalogue-state.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { CatalogueService } from 'softcat:services/catalogue.ts';
import { ConfigService } from 'softcat:services/config.ts';
import useSWR from 'swr';
import * as z from 'zod';

export const CataloguesCreate = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [onCreateError, setOnCreateError] = useState('');

    const CreateCatalogueSchema = z.object({
        configVersion: z.string(),
        name: z.string().regex(/^[\s\w().-]+$/, { message: intl.formatMessage({ id: 'CATALOGUES_FORM_NAME_VALIDATION' }) }),
        status: z.nativeEnum(CatalogueState),
        version: z.string().regex(/^[\d.]+$/, { message: intl.formatMessage({ id: 'CATALOGUES_CREATE_PAGE_FORM_VERSION_VALIDATION' }) }),
    });

    const { data: configVersionsData = [], isLoading: configVersionsDataIsLoading } = useSWR(
        { key: `${activeTerritory}-ConfigServiceFindAllConfigVersions` },
        ConfigService.findAllConfigVersions,
    );

    const onSubmit = async (values: z.infer<typeof CreateCatalogueSchema>) => {
        setSubmitting(true);
        setOnCreateError('');

        try {
            await CatalogueService.create(values);

            setSubmitting(false);

            return navigate('/catalogues');
        } catch (error) {
            setSubmitting(false);
            setOnCreateError((error as Error).message);
        }
    };

    const defaultValues = useMemo(() => {
        return { configVersion: configVersionsData[0] ?? '', name: '', status: CatalogueState.OPEN, version: '' };
    }, [configVersionsData]);

    return (
        <Form<z.infer<typeof CreateCatalogueSchema>> defaultValues={defaultValues} onSubmit={onSubmit} resolver={zodResolver(CreateCatalogueSchema)}>
            <div className="space-y-3 px-4">
                <Form.Header
                    description={<FormattedMessage id={'CATALOGUES_CREATE_PAGE_DESCRIPTION'} />}
                    title={<FormattedMessage id={'CATALOGUES_CREATE_PAGE_HEADING'} />}
                />

                <Form.Input label={<FormattedMessage id={'CATALOGUES_FORM_NAME_LABEL'} />} name={'name'} />

                <Form.Input label={<FormattedMessage id={'CATALOGUES_FORM_VERSION_LABEL'} />} name={'version'} />

                <Form.RadioGroup
                    label={<FormattedMessage id={'CATALOGUES_FORM_STATUS_LABEL'} />}
                    name={'status'}
                    options={Object.values(CatalogueState).map((state) => {
                        return { label: state, value: state };
                    })}
                />

                {!configVersionsDataIsLoading && configVersionsData.length === 0 ? (
                    <Form.StaticValue
                        label={<FormattedMessage id={'CATALOGUES_FORM_CONFIG_VERSION_LABEL'} />}
                        value={intl.formatMessage({ id: 'CATALOGUES_CREATE_PAGE_FORM_CONFIG_VERSION_NO_OPTIONS' })}
                    />
                ) : (
                    <Form.RadioGroup
                        label={<FormattedMessage id={'CATALOGUES_FORM_CONFIG_VERSION_LABEL'} />}
                        name={'configVersion'}
                        options={configVersionsData.map((configVersion) => {
                            return { label: configVersion, value: configVersion };
                        })}
                    />
                )}

                <Form.Footer
                    disabled={configVersionsData.length === 0}
                    isSubmitting={submitting}
                    onCancel={() => {
                        return navigate('/catalogues');
                    }}
                    submitError={onCreateError && <FormattedMessage id={'CATALOGUES_CREATE_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onCreateError }} />}
                    submitText={<FormattedMessage id={'BUTTON_CREATE'} />}
                />
            </div>
        </Form>
    );
};
